import {
  ArrowPathIcon,
  FireIcon,
  LockClosedIcon,
  LockOpenIcon,
  SparklesIcon
} from '@heroicons/react/20/solid';
import { CpuChipIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import {
  equipmentPlaceholderNote,
  KEPT_EQUIPMENT_PRODUCT_ID
} from 'utils/equipment-placeholder';
import { equipmentPlaceholderKind } from 'utils/equipment-placeholder-kind';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import store from 'app/store';
import Tag from 'components/common/tag';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger
} from 'components/layout/popover';
import Tooltip from 'components/layout/tooltip';
import EquipmentPlaceholderPhoto from 'components/products/equipment-placeholder-photo';
import ProductLink from 'components/products/product-link';
import ProductPhoto from 'components/products/product-photo';
import {
  AirHandlerProductType,
  CondenserProductType,
  SystemOptionCondenserType,
  ValidSystemType
} from 'data/types';
import { useUpsertCustomSystemMutation } from 'features/api/project-api';
import { setConfigurations } from 'features/configurations';
import { bumpCustomSystemsRevision } from 'features/systems';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { markCondensersStale } from 'utils/configurations';
import { abbreviateNumber, formatUSD } from 'utils/helpers';
import { isPtacIndoorFace } from 'utils/ptac-selected-system';
import { buildSeriesDisplayName } from 'utils/system-products';
import { deriveSwapOptionIds } from 'utils/swap-option-derivation';
import { describeSwapChanges, SwapChange, SwapLocks } from 'utils/system-swap';
import { buildCustomSystemEntries, getZoneNumberById } from 'utils/systems';
import { notification } from 'utils/ui';

type SystemMapProps = {
  condenser: SystemOptionCondenserType;
  editable?: boolean;
  hoverInfo?: boolean;
  systemOptionIndex?: number;
  configIdOverride?: string;
  defaultLocked?: boolean;
  showSpaces?: boolean;
  showTags?: boolean;
  showPrices?: boolean;
  showBTUs?: boolean;
  customerFacing?: boolean;
  trackingId?: string;
  onComponentSwap?: (
    type: 'condenser' | 'airHandler',
    productId: number,
    zoneId: string | undefined,
    locks: SwapLocks
  ) => boolean;
  hoveredZoneId?: string | null;
  onZoneHover?: (zoneId: string | null) => void;
  renderAfterCondenser?: () => ReactNode;
  renderAfterZone?: (zoneUniqueId: string) => ReactNode;
};

export default function SystemMap({
  condenser,
  editable,
  hoverInfo,
  systemOptionIndex,
  configIdOverride,
  defaultLocked,
  showSpaces,
  showTags,
  showPrices,
  showBTUs,
  customerFacing,
  trackingId,
  onComponentSwap,
  hoveredZoneId,
  onZoneHover,
  renderAfterCondenser,
  renderAfterZone
}: SystemMapProps) {
  const dispatch = useAppDispatch();
  const { condenserProducts, airHandlerProducts } = useAppSelector(
    (state) => state.data
  );
  const { configurations, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const { systemOptions } = useAppSelector((state) => state.systems);
  const [upsertCustomSystem] = useUpsertCustomSystemMutation();

  if (!condenser.selectedProduct) {
    return null;
  }

  const activeConfigId = configurations[activeConfig]?.uniqueId;
  const effectiveConfigId = configIdOverride ?? activeConfigId;
  const configOptions = useMemo(
    () => systemOptions[effectiveConfigId] ?? [],
    [systemOptions, effectiveConfigId]
  );
  const [validCondenserProducts, setValidCondenserProducts] = useState<
    CondenserProductType[]
  >([]);
  const [validAirHandlerProducts, setValidAirHandlerProducts] = useState<{
    [zoneId: string]: AirHandlerProductType[];
  }>({});
  const [condenserLocked, setCondenserLocked] = useState(
    defaultLocked ?? false
  );
  const [lockedZones, setLockedZones] = useState<string[]>(() =>
    defaultLocked
      ? condenser.zones
          .filter((zone) => zone.selectedProduct != null)
          .map((zone) => zone.uniqueId)
      : []
  );
  const [validSystemsForMap, setValidSystemsForMap] = useState<
    ValidSystemType[]
  >([]);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [airHandlerSearch, setAirHandlerSearch] = useState<
    Record<string, string>
  >({});
  const [condenserSearch, setCondenserSearch] = useState('');

  useEffect(() => {
    const sourceConfig = configurations.find(
      (c) => c.uniqueId === effectiveConfigId
    );
    setValidSystemsForMap(
      sourceConfig?.condensers.find((c) => c.uniqueId === condenser.uniqueId)
        ?.validSystems ?? []
    );
  }, [configurations, effectiveConfigId, condenser.uniqueId]);

  useEffect(() => {
    if (!isFirstRender) {
      setCondenserLocked(false);
      setLockedZones([]);
    }
    setIsFirstRender(false);
  }, [trackingId]);

  useEffect(() => {
    const swapZones = condenser.zones
      .filter((zone) => zone.selectedProduct != null)
      .map((zone) => ({
        uniqueId: zone.uniqueId,
        selectedProductId: zone.selectedProduct?.productId ?? null
      }));
    const { condenserProductIds, airHandlerProductIdsByZone } =
      deriveSwapOptionIds(
        validSystemsForMap,
        swapZones,
        condenser.selectedProduct?.productId,
        { condenserLocked, lockedZoneIds: lockedZones }
      );
    setValidCondenserProducts(
      condenserProducts.filter((product) =>
        condenserProductIds.includes(product.productId)
      )
    );
    const newValidAirHandlerProducts: {
      [zoneId: string]: AirHandlerProductType[];
    } = {};
    swapZones.forEach((zone) => {
      const zoneProductIds = airHandlerProductIdsByZone[zone.uniqueId] ?? [];
      newValidAirHandlerProducts[zone.uniqueId] = airHandlerProducts.filter(
        (product) => zoneProductIds.includes(product.productId)
      );
    });
    setValidAirHandlerProducts(newValidAirHandlerProducts);
  }, [
    condenser,
    condenserLocked,
    lockedZones,
    condenserProducts,
    airHandlerProducts,
    validSystemsForMap
  ]);

  // Legacy custom-save path: the jump-selected system can differ from the
  // displayed one beyond the clicked slot; announce those extra changes.
  const notifyDisplayedSystemChanges = (
    selectedSystem: ValidSystemType,
    clickedType: 'condenser' | 'airHandler',
    clickedZoneId?: string
  ) => {
    const changes: SwapChange[] = [];
    if (
      clickedType !== 'condenser' &&
      selectedSystem.condenserProductId !== condenser.selectedProduct?.productId
    ) {
      changes.push({
        slot: 0,
        zoneId: null,
        fromProductId: condenser.selectedProduct?.productId ?? null,
        toProductId: selectedSystem.condenserProductId ?? null
      });
    }
    condenser.zones
      .filter((zone) => zone.selectedProduct != null)
      .forEach((zone, zoneIndex) => {
        if (clickedType === 'airHandler' && zone.uniqueId === clickedZoneId) {
          return;
        }
        const zoneNumber = getZoneNumberById(selectedSystem, zone.uniqueId);
        const toProductId =
          zoneNumber === 0
            ? null
            : (selectedSystem[
                `airHandler${zoneNumber}ProductId` as keyof ValidSystemType
              ] as number | null | undefined) ?? null;
        const fromProductId = zone.selectedProduct?.productId ?? null;
        if (fromProductId === toProductId) return;
        changes.push({
          slot: zoneIndex + 1,
          zoneId: zone.uniqueId,
          fromProductId,
          toProductId
        });
      });

    if (changes.length === 0) return;
    notification(
      `Some components were changed to make this system work:\n${describeSwapChanges(
        changes,
        { condensers: condenserProducts, airHandlers: airHandlerProducts },
        new Map(condenser.zones.map((zone) => [zone.uniqueId, zone.name]))
      )}`,
      'warning'
    );
  };

  const handleCondenserClick = async (productId: number) => {
    if (
      onComponentSwap?.('condenser', productId, undefined, {
        condenserLocked,
        lockedZoneIds: lockedZones
      })
    )
      return;
    if (
      editable &&
      systemOptionIndex !== undefined &&
      configOptions.length > 0
    ) {
      const selectedSystem = validSystemsForMap
        .filter((system) => {
          let valid = true;
          lockedZones.forEach((zoneId) => {
            const zoneNumber = getZoneNumberById(system, zoneId);
            if (
              system[`airHandler${zoneNumber}ProductId`] !==
              condenser.zones.find((zone) => zone.uniqueId === zoneId)
                ?.selectedProduct.productId
            ) {
              valid = false;
            }
          });
          return valid;
        })
        .find((system) => system.condenserProductId === productId);
      if (selectedSystem) {
        notifyDisplayedSystemChanges(selectedSystem, 'condenser');
        const systemsForNewOption: ValidSystemType[] = [];
        configOptions[systemOptionIndex].configuration.condensers.forEach(
          (cond) => {
            if (cond.uniqueId === condenser.uniqueId) {
              systemsForNewOption.push(selectedSystem);
            } else {
              const otherValidSystems =
                configurations
                  .find(
                    (c) =>
                      c.uniqueId ===
                      configOptions[systemOptionIndex].configuration.uniqueId
                  )
                  ?.condensers.find((c) => c.uniqueId === cond.uniqueId)
                  ?.validSystems ?? [];
              const system = otherValidSystems.find(
                (sys) =>
                  sys.condenserProductId === cond.selectedProduct.productId &&
                  cond.zones.every((zone) => {
                    const zoneNumber = getZoneNumberById(sys, zone.uniqueId);
                    return (
                      sys[`airHandler${zoneNumber}ProductId`] ===
                      cond.zones.find((z) => z.uniqueId === zone.uniqueId)
                        ?.selectedProduct.productId
                    );
                  })
              );
              if (system) {
                systemsForNewOption.push(system);
              }
            }
          }
        );
        const config = configurations.find(
          (c) =>
            c.uniqueId ===
            configOptions[systemOptionIndex].configuration.uniqueId
        );
        if (config) {
          const currentSystem = configOptions[systemOptionIndex];
          const currentSystemIsCustom = currentSystem.tags.includes('Custom');
          let systemNumber = 1;
          while (
            configOptions.some(
              // eslint-disable-next-line @typescript-eslint/no-loop-func
              (o) =>
                o.configuration.uniqueId ===
                  currentSystem.configuration.uniqueId &&
                o.name === `Custom System ${systemNumber}`
            )
          ) {
            systemNumber += 1;
          }
          const name = currentSystemIsCustom
            ? currentSystem.name
            : `Custom System ${systemNumber}`;
          const entries = buildCustomSystemEntries(systemsForNewOption, config);
          try {
            await upsertCustomSystem({
              uniqueId: currentSystemIsCustom
                ? currentSystem.uniqueId
                : undefined,
              configurationUniqueId: config.uniqueId,
              name,
              entries
            }).unwrap();
            dispatch(bumpCustomSystemsRevision({ configId: config.uniqueId }));
            const latest = store.getState().configurations.configurations;
            dispatch(
              setConfigurations(
                latest.map((c) =>
                  c.uniqueId === config.uniqueId ? markCondensersStale(c) : c
                )
              )
            );
            setCondenserLocked(true);
          } catch (err) {
            console.error(
              'Failed to save custom system from system-map swap',
              err
            );
          }
        } else {
          console.error('System option config not found');
        }
      } else {
        console.error('No system found');
        notification(
          "This combination isn't available with your current filters.",
          'warning'
        );
      }
    } else {
      console.error('No system option index provided');
    }
  };

  const handleAirHandlerClick = async (zoneId: string, productId: number) => {
    if (
      onComponentSwap?.('airHandler', productId, zoneId, {
        condenserLocked,
        lockedZoneIds: lockedZones
      })
    )
      return;
    if (
      editable &&
      systemOptionIndex !== undefined &&
      configOptions.length > 0
    ) {
      const selectedSystem = validSystemsForMap
        .filter((system) => {
          if (!condenserLocked) {
            return true;
          }
          if (
            system.condenserProductId === condenser.selectedProduct.productId
          ) {
            return true;
          }
          return false;
        })
        .filter((system) => {
          let valid = true;
          lockedZones
            .filter((id) => id !== zoneId)
            .forEach((lockedZoneId) => {
              const lockedZoneNumber = getZoneNumberById(system, lockedZoneId);
              if (
                system[`airHandler${lockedZoneNumber}ProductId`] !==
                condenser.zones.find((zone) => zone.uniqueId === lockedZoneId)
                  ?.selectedProduct.productId
              ) {
                valid = false;
              }
            });
          return valid;
        })
        .find((system) => {
          const zoneNumber = getZoneNumberById(system, zoneId);
          return (
            zoneNumber > 0 &&
            system[`airHandler${zoneNumber}ProductId`] === productId
          );
        });
      if (selectedSystem) {
        notifyDisplayedSystemChanges(selectedSystem, 'airHandler', zoneId);
        const systemsForNewOption: ValidSystemType[] = [];
        configOptions[systemOptionIndex].configuration.condensers.forEach(
          (cond) => {
            if (cond.uniqueId === condenser.uniqueId) {
              systemsForNewOption.push(selectedSystem);
            } else {
              const otherValidSystems =
                configurations
                  .find(
                    (c) =>
                      c.uniqueId ===
                      configOptions[systemOptionIndex].configuration.uniqueId
                  )
                  ?.condensers.find((c) => c.uniqueId === cond.uniqueId)
                  ?.validSystems ?? [];
              const system = otherValidSystems.find(
                (sys) =>
                  sys.condenserProductId === cond.selectedProduct.productId &&
                  cond.zones.every((zone) => {
                    const zNumber = getZoneNumberById(sys, zone.uniqueId);
                    return (
                      sys[`airHandler${zNumber}ProductId`] ===
                      cond.zones.find((z) => z.uniqueId === zone.uniqueId)
                        ?.selectedProduct.productId
                    );
                  })
              );
              if (system) {
                systemsForNewOption.push(system);
              }
            }
          }
        );
        const config = configurations.find(
          (c) =>
            c.uniqueId ===
            configOptions[systemOptionIndex].configuration.uniqueId
        );
        if (config) {
          const currentSystem = configOptions[systemOptionIndex];
          const currentSystemIsCustom = currentSystem.tags.includes('Custom');
          let systemNumber = 1;
          while (
            configOptions.some(
              // eslint-disable-next-line @typescript-eslint/no-loop-func
              (o) =>
                o.configuration.uniqueId ===
                  currentSystem.configuration.uniqueId &&
                o.name === `Custom System ${systemNumber}`
            )
          ) {
            systemNumber += 1;
          }
          const name = currentSystemIsCustom
            ? currentSystem.name
            : `Custom System ${systemNumber}`;
          const entries = buildCustomSystemEntries(systemsForNewOption, config);
          try {
            await upsertCustomSystem({
              uniqueId: currentSystemIsCustom
                ? currentSystem.uniqueId
                : undefined,
              configurationUniqueId: config.uniqueId,
              name,
              entries
            }).unwrap();
            dispatch(bumpCustomSystemsRevision({ configId: config.uniqueId }));
            const latest = store.getState().configurations.configurations;
            dispatch(
              setConfigurations(
                latest.map((c) =>
                  c.uniqueId === config.uniqueId ? markCondensersStale(c) : c
                )
              )
            );
            setLockedZones((oldValues) => [...oldValues, zoneId]);
          } catch (err) {
            console.error(
              'Failed to save custom system from system-map swap',
              err
            );
          }
        } else {
          console.error('System option config not found');
        }
      } else {
        console.error('No system found');
        notification(
          "This combination isn't available with your current filters.",
          'warning'
        );
      }
    } else {
      console.error('No system option index provided');
    }
  };

  const toggleZoneLock = (zoneId: string) => {
    if (lockedZones.includes(zoneId)) {
      setLockedZones((oldValues) => oldValues.filter((v) => v !== zoneId));
    } else {
      setLockedZones((oldValues) => [...oldValues, zoneId]);
    }
  };

  const indoorZones = condenser.zones.filter(
    (zone) =>
      zone.selectedProduct != null && !isPtacIndoorFace(zone.selectedProduct)
  );

  return (
    <div key={condenser.uniqueId} className='flex flex-col'>
      <div className='flex'>
        {indoorZones.length > 0 && (
          <div
            data-testid='system-map-connector'
            className='flex flex-col pr-2 pt-2'
          >
            <div className='h-3 w-3 rounded-full bg-gray-300' />
            <div className='ml-[4px] grow border-l-4 border-gray-300' />
          </div>
        )}
        <div className='flex grow flex-col gap-2'>
          <div className='flex flex-wrap items-center gap-2'>
            <CpuChipIcon className='h-6 w-6 text-gray-800' />
            <span className='text-xl font-semibold'>{condenser.name}</span>
            {editable && (
              <>
                <Popover placement='bottom-start'>
                  <PopoverTrigger className='relative text-gray-400 hover:text-gray-500'>
                    <ArrowPathIcon className='h-6 w-6' />
                    <Tooltip text='Change Condenser' />
                  </PopoverTrigger>
                  <PopoverContent className='z-50 max-h-[500px] overflow-auto rounded-lg bg-white shadow-lg ring-1 ring-black/5'>
                    {(() => {
                      const currentQuery = condenserSearch.toLowerCase();
                      const list = validCondenserProducts.filter(
                        (p) =>
                          currentQuery === '' ||
                          p.model.toLowerCase().includes(currentQuery) ||
                          (p.series ?? '')
                            .toLowerCase()
                            .includes(currentQuery) ||
                          (p.name ?? '').toLowerCase().includes(currentQuery)
                      );
                      return (
                        <div className='flex flex-col'>
                          <div className='sticky top-0 z-10 bg-white p-2'>
                            <input
                              type='text'
                              value={condenserSearch}
                              onChange={(e) =>
                                setCondenserSearch(e.target.value)
                              }
                              placeholder='Search model or name'
                              className='w-full rounded-md border border-gray-300 p-2 text-sm'
                            />
                          </div>
                          {list.length > 0 ? (
                            <div className='mx-2 mb-2 table'>
                              <div className='table-row-group'>
                                {list
                                  .toSorted((a, b) => a.seer - b.seer)
                                  .toSorted(
                                    (a, b) => a.coolingBTU - b.coolingBTU
                                  )
                                  .map((product) => (
                                    <PopoverClose
                                      className='group table-row rounded-md px-2 py-1 font-semibold'
                                      onClick={() =>
                                        handleCondenserClick(product.productId)
                                      }
                                      key={product.productId}
                                    >
                                      <div className='table-cell rounded-l-md p-1 align-middle group-hover:bg-gray-100'>
                                        <ProductPhoto
                                          product={product}
                                          size='small'
                                        />
                                      </div>
                                      <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                        <span className='whitespace-nowrap'>
                                          {product.model}
                                        </span>
                                      </div>
                                      <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                        <Tag noWrap>
                                          <span>
                                            {abbreviateNumber(
                                              product.coolingBTU
                                            )}{' '}
                                            BTU
                                          </span>{' '}
                                          {product.coolingBTU !==
                                            condenser.selectedProduct
                                              .coolingBTU && (
                                            <span>
                                              (
                                              <span
                                                className={
                                                  product.coolingBTU <
                                                  condenser.selectedProduct
                                                    .coolingBTU
                                                    ? 'text-red-600'
                                                    : 'text-green-600'
                                                }
                                              >
                                                {product.coolingBTU <
                                                condenser.selectedProduct
                                                  .coolingBTU
                                                  ? '-'
                                                  : '+'}
                                                {abbreviateNumber(
                                                  Math.abs(
                                                    product.coolingBTU -
                                                      condenser.selectedProduct
                                                        .coolingBTU
                                                  )
                                                )}
                                              </span>
                                              )
                                            </span>
                                          )}
                                        </Tag>
                                      </div>
                                      <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                        <Tag noWrap>
                                          <span>{product.seer} SEER</span>{' '}
                                          {product.seer !==
                                            condenser.selectedProduct.seer && (
                                            <span>
                                              (
                                              <span
                                                className={
                                                  product.seer <
                                                  condenser.selectedProduct.seer
                                                    ? 'text-red-600'
                                                    : 'text-green-600'
                                                }
                                              >
                                                {product.seer <
                                                condenser.selectedProduct.seer
                                                  ? '-'
                                                  : '+'}
                                                {Math.round(
                                                  Math.abs(
                                                    product.seer -
                                                      condenser.selectedProduct
                                                        .seer
                                                  ) * 10
                                                ) / 10}
                                              </span>
                                              )
                                            </span>
                                          )}
                                        </Tag>
                                      </div>
                                      <div className='table-cell rounded-r-md p-1 align-middle group-hover:bg-gray-100'>
                                        <span
                                          className={`text-sm font-semibold ${
                                            product.price <=
                                            condenser.selectedProduct.price
                                              ? 'text-green-600'
                                              : 'text-red-600'
                                          }`}
                                        >
                                          {product.price <
                                          condenser.selectedProduct.price
                                            ? '-'
                                            : '+'}
                                          {formatUSD(
                                            Math.abs(
                                              product.price -
                                                condenser.selectedProduct.price
                                            )
                                          )}
                                        </span>
                                      </div>
                                    </PopoverClose>
                                  ))}
                              </div>
                            </div>
                          ) : (
                            <div className='max-w-[400px] px-3 pb-3'>
                              No other condensers available.
                              {lockedZones.length > 0 &&
                                ' Try unlocking zones to see more options.'}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </PopoverContent>
                </Popover>
                <button
                  type='button'
                  className='relative text-gray-300 hover:text-gray-400'
                  onClick={() => setCondenserLocked((oldValue) => !oldValue)}
                >
                  {condenserLocked ? (
                    <LockClosedIcon className='h-6 w-6 text-gray-400' />
                  ) : (
                    <LockOpenIcon className='h-6 w-6' />
                  )}
                  <Tooltip
                    text={
                      condenserLocked ? 'Unlock Condenser' : 'Lock Condenser'
                    }
                  />
                </button>
              </>
            )}
          </div>
          <div className='flex grow items-start gap-3'>
            {condenser.selectedProduct.productId ===
            KEPT_EQUIPMENT_PRODUCT_ID ? (
              <EquipmentPlaceholderPhoto
                kind={equipmentPlaceholderKind(
                  condenser.selectedProduct,
                  'outdoor'
                )}
              />
            ) : (
              <ProductPhoto product={condenser.selectedProduct} size='medium' />
            )}
            <div className='flex grow flex-col gap-1'>
              <div className='flex w-full items-center'>
                <div className='flex grow flex-wrap items-center gap-2'>
                  {condenser.selectedProduct.productId ===
                  KEPT_EQUIPMENT_PRODUCT_ID ? (
                    <span className='text-lg font-semibold text-gray-500'>
                      {condenser.selectedProduct.name}
                    </span>
                  ) : (
                    <span className='text-lg font-semibold'>
                      {buildSeriesDisplayName(condenser.selectedProduct) ??
                        condenser.selectedProduct.series ??
                        'Unknown Series'}
                    </span>
                  )}
                  {showTags &&
                    condenser.selectedProduct.productId !==
                      KEPT_EQUIPMENT_PRODUCT_ID && (
                      <>
                        <Tag
                          text={`${abbreviateNumber(
                            Math.round(
                              condenser.selectedProduct.coolingBTU / 1000
                            ) * 1000
                          )}`}
                        />
                        <Tag text={`${condenser.selectedProduct.seer} SEER`} />
                      </>
                    )}
                </div>
                {showPrices &&
                  condenser.selectedProduct.productId !==
                    KEPT_EQUIPMENT_PRODUCT_ID && (
                    <span className='text-right text-lg font-bold'>
                      {formatUSD(condenser.selectedProduct.price, true)}
                    </span>
                  )}
              </div>
              {condenser.selectedProduct.productId ===
              KEPT_EQUIPMENT_PRODUCT_ID ? (
                <span className='text-sm italic text-gray-400'>
                  {equipmentPlaceholderNote(condenser.selectedProduct)}
                </span>
              ) : (
                <ProductLink
                  type='condenser'
                  product={condenser.selectedProduct}
                  hoverInfo={hoverInfo ?? editable}
                  fullName={!editable}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {renderAfterCondenser?.()}
      {indoorZones
        .map((zone, index) => ({
          zone,
          isLast: index === indoorZones.length - 1
        }))
        .map(({ zone, isLast }) => (
          <div
            key={zone.uniqueId}
            className='flex items-start gap-2'
            style={{
              borderRadius: '8px',
              transition: 'background-color 150ms',
              backgroundColor:
                hoveredZoneId === zone.uniqueId
                  ? 'rgba(59, 130, 246, 0.08)'
                  : undefined
            }}
            onMouseEnter={() => onZoneHover?.(zone.uniqueId)}
            onMouseLeave={() => onZoneHover?.(null)}
          >
            <div className='relative flex items-start self-stretch'>
              {!isLast && (
                <div className='absolute left-[4px] h-full border-l-4 border-gray-300' />
              )}
              <div className='mb-[4px] ml-[4px] w-7 rounded-bl-2xl border-b-4 border-l-4 border-gray-300 pt-10' />
              <div className='mt-[36px] h-3 w-3 rounded-full bg-gray-300' />
            </div>
            <div className='flex grow flex-col gap-2 pt-7'>
              <div className='flex flex-wrap items-center gap-2'>
                <RectangleStackIcon className='h-6 w-6 shrink-0 text-gray-800' />
                <span className='shrink-0 text-xl font-semibold'>
                  {zone.name}
                </span>
                {editable && (
                  <>
                    <Popover placement='bottom-start'>
                      <PopoverTrigger className='relative text-gray-400 hover:text-gray-500'>
                        <ArrowPathIcon className='h-6 w-6' />
                        <Tooltip text='Change Air Handler' />
                      </PopoverTrigger>
                      <PopoverContent className='z-50 max-h-[500px] overflow-auto rounded-lg bg-white shadow-lg ring-1 ring-black/5'>
                        {(() => {
                          const currentQuery = (
                            airHandlerSearch[zone.uniqueId] ?? ''
                          ).toLowerCase();
                          const zoneProducts =
                            validAirHandlerProducts[zone.uniqueId] ?? [];
                          const list = zoneProducts.filter(
                            (p) =>
                              currentQuery === '' ||
                              p.model.toLowerCase().includes(currentQuery) ||
                              p.name.toLowerCase().includes(currentQuery)
                          );
                          return (
                            <div className='relative flex flex-col'>
                              <div className='sticky top-0 z-10 bg-white p-2'>
                                <input
                                  type='text'
                                  value={airHandlerSearch[zone.uniqueId] ?? ''}
                                  onChange={(e) =>
                                    setAirHandlerSearch((prev) => ({
                                      ...prev,
                                      [zone.uniqueId]: e.target.value
                                    }))
                                  }
                                  placeholder='Search model or name'
                                  className='w-full rounded-md border border-gray-300 p-2 text-sm'
                                />
                              </div>
                              {list.length > 0 ? (
                                <div className='mx-2 mb-2 table'>
                                  <div className='table-row-group'>
                                    {list
                                      .toSorted(
                                        (a, b) => a.coolingBTU - b.coolingBTU
                                      )
                                      .sort((a, b) =>
                                        a.type.localeCompare(b.type)
                                      )
                                      .map((product) => (
                                        <PopoverClose
                                          key={product.productId}
                                          className='group table-row rounded-md px-2 py-1 font-semibold'
                                          onClick={() =>
                                            handleAirHandlerClick(
                                              zone.uniqueId,
                                              product.productId
                                            )
                                          }
                                        >
                                          <div className='table-cell rounded-l-md p-1 align-middle group-hover:bg-gray-100'>
                                            <ProductPhoto
                                              product={product}
                                              size='small'
                                            />
                                          </div>
                                          <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                            <span className='whitespace-nowrap'>
                                              {product.model}
                                            </span>
                                          </div>
                                          <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                            <Tag noWrap text={product.type} />
                                          </div>
                                          <div className='table-cell p-1 align-middle group-hover:bg-gray-100'>
                                            <Tag noWrap>
                                              <span>
                                                {abbreviateNumber(
                                                  product.coolingBTU
                                                )}{' '}
                                                BTU
                                              </span>{' '}
                                              {product.coolingBTU !==
                                                zone.selectedProduct
                                                  .coolingBTU && (
                                                <span>
                                                  (
                                                  <span
                                                    className={
                                                      product.coolingBTU <
                                                      zone.selectedProduct
                                                        .coolingBTU
                                                        ? 'text-red-600'
                                                        : 'text-green-600'
                                                    }
                                                  >
                                                    {product.coolingBTU <
                                                    zone.selectedProduct
                                                      .coolingBTU
                                                      ? '-'
                                                      : '+'}
                                                    {abbreviateNumber(
                                                      Math.abs(
                                                        product.coolingBTU -
                                                          zone.selectedProduct
                                                            .coolingBTU
                                                      )
                                                    )}
                                                  </span>
                                                  )
                                                </span>
                                              )}
                                            </Tag>
                                          </div>
                                          <div className='table-cell rounded-r-md p-1 align-middle group-hover:bg-gray-100'>
                                            <span
                                              className={`text-sm font-semibold ${
                                                product.price <=
                                                zone.selectedProduct.price
                                                  ? 'text-green-600'
                                                  : 'text-red-600'
                                              }`}
                                            >
                                              {product.price <
                                              zone.selectedProduct.price
                                                ? '-'
                                                : '+'}
                                              {formatUSD(
                                                Math.abs(
                                                  product.price -
                                                    zone.selectedProduct.price
                                                )
                                              )}
                                            </span>
                                          </div>
                                        </PopoverClose>
                                      ))}
                                  </div>
                                </div>
                              ) : (
                                <div className='max-w-[400px] px-3 pb-3'>
                                  No other air handlers available.
                                  {(condenserLocked ||
                                    lockedZones.length > 0) &&
                                    ' Try unlocking condenser or other zones to see more options.'}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </PopoverContent>
                    </Popover>
                    <button
                      type='button'
                      className='relative text-gray-300 hover:text-gray-400'
                      onClick={() => toggleZoneLock(zone.uniqueId)}
                    >
                      {lockedZones.includes(zone.uniqueId) ? (
                        <LockClosedIcon className='h-6 w-6 text-gray-400' />
                      ) : (
                        <LockOpenIcon className='h-6 w-6' />
                      )}
                      <Tooltip
                        text={
                          lockedZones.includes(zone.uniqueId)
                            ? 'Unlock Zone'
                            : 'Lock Zone'
                        }
                      />
                    </button>
                  </>
                )}
                {showBTUs && zone.zoneItems.length > 0 && (
                  <>
                    <Tag>
                      <span className='inline-flex items-center gap-1'>
                        {!customerFacing ? (
                          <>
                            <div className='h-3 w-3 rounded-full bg-blue-400' />
                            {abbreviateNumber(
                              zone.zoneItems.reduce(
                                (acc, item) => acc + item.BTUs.cooling,
                                0
                              )
                            )}
                          </>
                        ) : (
                          <>
                            <SparklesIcon className='h-3 w-3 text-sky-500' />
                            <span>
                              {(() => {
                                const totalCoolingBTUs = zone.zoneItems.reduce(
                                  (acc, item) => acc + item.BTUs.cooling,
                                  0
                                );
                                const coolingCapacity =
                                  zone.selectedProduct.coolingBTU;
                                return `${Math.min(
                                  (coolingCapacity / totalCoolingBTUs) * 100,
                                  100
                                ).toFixed(2)}%`;
                              })()}
                            </span>
                          </>
                        )}
                      </span>
                    </Tag>
                    <Tag>
                      <span className='inline-flex items-center gap-1'>
                        {!customerFacing ? (
                          <>
                            <div className='h-3 w-3 rounded-full bg-orange-400' />
                            {abbreviateNumber(
                              zone.zoneItems.reduce(
                                (acc, item) => acc + item.BTUs.heating,
                                0
                              )
                            )}
                          </>
                        ) : (
                          <>
                            <FireIcon className='h-3 w-3 text-orange-500' />
                            <span>
                              {(() => {
                                const totalHeatingBTUs = zone.zoneItems.reduce(
                                  (acc, item) => acc + item.BTUs.heating,
                                  0
                                );
                                const heatingCapacity =
                                  zone.selectedProduct.heatingBTU;
                                return `${Math.min(
                                  (heatingCapacity / totalHeatingBTUs) * 100,
                                  100
                                ).toFixed(2)}%`;
                              })()}
                            </span>
                          </>
                        )}
                      </span>
                    </Tag>
                  </>
                )}
                {showSpaces &&
                  zone.type !== 'ducted' &&
                  zone.zoneItems.length > 0 &&
                  zone.zoneItems.map((item) => (
                    <Tag key={item.uniqueId} text={item.name} />
                  ))}
              </div>
              <div className='flex grow items-start gap-3'>
                {zone.selectedProduct.productId ===
                KEPT_EQUIPMENT_PRODUCT_ID ? (
                  <EquipmentPlaceholderPhoto
                    kind={equipmentPlaceholderKind(
                      zone.selectedProduct,
                      'indoor'
                    )}
                  />
                ) : (
                  <ProductPhoto product={zone.selectedProduct} size='medium' />
                )}
                <div className='flex grow flex-col gap-1'>
                  <div className='flex w-full items-center'>
                    <div className='flex grow flex-wrap items-center gap-2'>
                      {zone.selectedProduct.productId ===
                      KEPT_EQUIPMENT_PRODUCT_ID ? (
                        <span className='text-lg font-semibold text-gray-500'>
                          {zone.selectedProduct.name}
                        </span>
                      ) : (
                        <span className='text-lg font-semibold'>
                          {zone.selectedProduct.coolingBTU
                            ? `${abbreviateNumber(
                                zone.selectedProduct.coolingBTU
                              )} `
                            : ''}
                          {zone.selectedProduct.type}
                        </span>
                      )}
                    </div>
                    {showPrices &&
                      zone.selectedProduct.productId !==
                        KEPT_EQUIPMENT_PRODUCT_ID && (
                        <span className='text-right text-lg font-bold'>
                          {formatUSD(zone.selectedProduct.price, true)}
                        </span>
                      )}
                  </div>
                  {zone.selectedProduct.productId ===
                  KEPT_EQUIPMENT_PRODUCT_ID ? (
                    <span className='text-sm italic text-gray-400'>
                      {equipmentPlaceholderNote(zone.selectedProduct)}
                    </span>
                  ) : (
                    <ProductLink
                      type='air handler'
                      product={zone.selectedProduct}
                      hoverInfo={hoverInfo ?? editable}
                      fullName={!editable}
                    />
                  )}
                </div>
              </div>
              {zone.blowerProduct && (
                <div className='mt-4 flex grow items-start gap-3'>
                  {zone.blowerProduct.productId ===
                  KEPT_EQUIPMENT_PRODUCT_ID ? (
                    <EquipmentPlaceholderPhoto
                      kind={equipmentPlaceholderKind(
                        zone.blowerProduct,
                        'blower'
                      )}
                    />
                  ) : (
                    <ProductPhoto product={zone.blowerProduct} size='medium' />
                  )}
                  <div className='flex grow flex-col gap-1'>
                    <div className='flex w-full items-center'>
                      <div className='flex grow flex-wrap items-center gap-2'>
                        {zone.blowerProduct.productId ===
                        KEPT_EQUIPMENT_PRODUCT_ID ? (
                          <span className='text-lg font-semibold text-gray-500'>
                            {zone.blowerProduct.name}
                          </span>
                        ) : (
                          <span className='text-lg font-semibold'>
                            {zone.blowerProduct.heatingBTU > 0
                              ? `${abbreviateNumber(
                                  zone.blowerProduct.heatingBTU
                                )} `
                              : ''}
                            {zone.blowerProduct.type || 'Blower'}
                          </span>
                        )}
                      </div>
                      {showPrices &&
                        zone.blowerProduct.productId !==
                          KEPT_EQUIPMENT_PRODUCT_ID && (
                          <span className='text-right text-lg font-bold'>
                            {formatUSD(zone.blowerProduct.price, true)}
                          </span>
                        )}
                    </div>
                    {zone.blowerProduct.productId ===
                    KEPT_EQUIPMENT_PRODUCT_ID ? (
                      <span className='text-sm italic text-gray-400'>
                        {equipmentPlaceholderNote(zone.blowerProduct)}
                      </span>
                    ) : (
                      <ProductLink
                        type='air handler'
                        product={zone.blowerProduct}
                        fullName
                      />
                    )}
                  </div>
                </div>
              )}
              {renderAfterZone?.(zone.uniqueId)}
            </div>
          </div>
        ))}
    </div>
  );
}
